import { useEffect, useRef, useState, useCallback } from "react";

const OFFICES = [
  { id: "jc", country: "USA",   flag: "US", city: "Jersey City, NJ", lat: 40.72,  lon: -74.04, timezone: "GMT-5" },
  { id: "bd", country: "UK",    flag: "GB", city: "Bradford",        lat: 53.79,  lon: -1.75,  timezone: "GMT+0" },
  { id: "hy", country: "India", flag: "IN", city: "Hyderabad",       lat: 17.39,  lon: 78.49,  timezone: "GMT+5:30" },
  { id: "ch", country: "India", flag: "IN", city: "Chennai",         lat: 13.08,  lon: 80.27,  timezone: "GMT+5:30" },
  { id: "kl", country: "USA",   flag: "US", city: "Kalispell, MT",   lat: 48.196, lon: -114.31, timezone: "GMT-7" },
];

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

const GEOJSON_URL = "https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@master/geojson/ne_110m_admin_0_countries.geojson";
const GEOJSON_URL_FALLBACK = "https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson";

let leafletLoadingPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadingPromise) return leafletLoadingPromise;

  leafletLoadingPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existingScript = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existingScript) {
      if (window.L) return resolve(window.L);
      existingScript.addEventListener("load", () => resolve(window.L));
      existingScript.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return leafletLoadingPromise;
}

function fetchGeoJsonWithFallback() {
  return fetch(GEOJSON_URL)
    .then((r) => {
      if (!r.ok) throw new Error(`Primary GeoJSON source failed: ${r.status}`);
      return r.json();
    })
    .catch((err) => {
      console.warn("Primary GeoJSON source failed, trying fallback…", err);
      return fetch(GEOJSON_URL_FALLBACK).then((r) => {
        if (!r.ok) throw new Error(`Fallback GeoJSON source failed: ${r.status}`);
        return r.json();
      });
    });
}

export default function GlobalLocations({ selectedOffice }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [satellite, setSatellite] = useState(false);
  const tileLayersRef = useRef({});

  const fitAllOffices = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    setSatellite(false);

    const layers = tileLayersRef.current;

    if (layers.satellite && map.hasLayer(layers.satellite)) {
      map.removeLayer(layers.satellite);
    }
    if (layers.satelliteLabels && map.hasLayer(layers.satelliteLabels)) {
      map.removeLayer(layers.satelliteLabels);
    }
    if (layers.geoJsonLayer && !map.hasLayer(layers.geoJsonLayer)) {
      layers.geoJsonLayer.addTo(map);
      layers.geoJsonLayer.bringToBack();
    }

    map.flyTo([25, -20], 1, { duration: 1.2 });
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      loadLeaflet(),
      fetchGeoJsonWithFallback(),
    ])
      .then(([L, worldGeoJson]) => {
        if (cancelled || !mapElRef.current || mapRef.current) return;

        const map = L.map(mapElRef.current, {
          center: [25, -20],
          zoom: 2,
          minZoom: 0,
          maxZoom: 18,
          worldCopyJump: true,
          zoomControl: true,
          scrollWheelZoom: true,
          attributionControl: true,
        });

        map.getContainer().style.background = "#ffffff";

        const geoJsonLayer = L.geoJSON(worldGeoJson, {
          style: {
            fillColor: "#dbe7f5",
            fillOpacity: 1,
            color: "#ffffff",
            weight: 1,
          },
          interactive: false,
        });
        geoJsonLayer.addTo(map);

        const satLayer = L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          {
            attribution:
              "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
            maxZoom: 19,
          }
        );
          
        const satLabels = L.tileLayer(
          "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
          {
            attribution: "Labels © Esri",
            pane: "overlayPane",
            maxZoom: 19,
          }
        );

        tileLayersRef.current = { geoJsonLayer, satellite: satLayer, satelliteLabels: satLabels };

        setSatellite(false);

        const overlay = L.layerGroup().addTo(map);
        layerRef.current = overlay;

        OFFICES.forEach((o) => {
          const icon = L.divIcon({
            className: "office-marker-icon",
            html: `<div class="office-dot"></div>`,
            iconSize: [14, 14],
            iconAnchor: [7, 7],
            popupAnchor: [0, -10],
          });

          const marker = L.marker([o.lat, o.lon], {
            icon,
            riseOnHover: true,
          }).addTo(overlay);

          markersRef.current[o.id] = marker;

          const tooltipHtml = `
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="font-size:10px;font-weight:700;color:#6b7a99;background:#1c2333;border:1px solid #2a3347;border-radius:3px;padding:1px 5px;">${o.flag}</span>
              <span style="font-size:13px;font-weight:600;color:#e8f0ff;">${o.country}</span>
            </div>
            <div style="font-size:12px;color:#8ba4cc;margin-bottom:2px;">${o.city}</div>
            <div style="font-size:11px;color:#4d6080;">${o.timezone}</div>
          `;
          marker.bindTooltip(tooltipHtml, {
            direction: "top",
            offset: [0, -4],
            opacity: 1,
            className: "office-tooltip",
          });

          marker.on("click", () => {
            map.flyTo([o.lat, o.lon], 8, { duration: 1.1 });
          });
        });

        map.setView([25, -20], 1);

        mapRef.current = map;
        setReady(true);

        const resizeObserver = new ResizeObserver(() => { map.invalidateSize(); });
        resizeObserver.observe(mapElRef.current);
        requestAnimationFrame(() => map.invalidateSize());
        mapRef.current._resizeObserver = resizeObserver;
      })
      .catch((err) => {
        console.error("Failed to initialize map:", err);
        if (!cancelled) setLoadError(err.message || "Failed to load map");
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        if (mapRef.current._resizeObserver) mapRef.current._resizeObserver.disconnect();
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedOffice) return;

    const map = mapRef.current;
    const marker = markersRef.current[selectedOffice];
    if (!map || !marker) return;

    const office = OFFICES.find(o => o.id === selectedOffice);

    map.flyTo([office.lat, office.lon], 10, { duration: 1.5, easeLinearity: 0.25 });

    setTimeout(() => {
      marker.openTooltip();
      setTimeout(() => { marker.closeTooltip(); }, 3000);
    }, 1200);
  }, [selectedOffice]);

  const toggleSatellite = useCallback(() => {
    const map = mapRef.current;
    const layers = tileLayersRef.current;

    if (!map || !layers.geoJsonLayer || !layers.satellite || !layers.satelliteLabels) return;

    setSatellite((prev) => {
      const next = !prev;
      if (next) {
        map.removeLayer(layers.geoJsonLayer);
        layers.satellite.addTo(map);
        layers.satelliteLabels.addTo(map);
        layers.satellite.bringToBack();
      } else {
        map.removeLayer(layers.satellite);
        map.removeLayer(layers.satelliteLabels);
        layers.geoJsonLayer.addTo(map);
        layers.geoJsonLayer.bringToBack();
      }
      return next;
    });
  }, []);

return (
  <div
    className="relative w-full h-[260px] md:h-[400px] lg:h-[200px] rounded-xl overflow-hidden"
    style={{
      position: "relative",
      // ✅ FIX: isolate this component's stacking context so Leaflet's internal
      // z-indices (400,600) cannot escape and overlap the navbar above.
      // zIndex: 0 + isolation: "isolate" creates a new stacking context,
      // trapping all children (map tiles, controls, tooltips) inside this wrapper.
      zIndex: 0,
      isolation: "isolate",
      width: "100%",
      minHeight: "260px",
      borderRadius: "16px",
      overflow: "hidden",
      background: "#ffffff",
      boxShadow: "0 0 60px rgba(30,80,180,0.35), 0 0 120px rgba(10,40,120,0.18)",
      border: "1px solid rgba(100,160,255,0.18)",
    }}
  >
      {/* Interactive Leaflet Map */}
      <div ref={mapElRef} style={{ width: "100%", height: "100%" }} />

      {!ready && !loadError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#8ba4cc",
            fontSize: "13px",
            background: "#ffffff",
          }}
        >
          Loading map…
        </div>
      )}

      {loadError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#c0392b",
            fontSize: "13px",
            background: "#ffffff",
            textAlign: "center",
            padding: "20px",
          }}
        >
          Map failed to load: {loadError}
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          gap: "8px",
          zIndex: 9999,
          pointerEvents: "auto",
        }}
      >
        <button
          onClick={toggleSatellite}
          style={{
            background: "rgba(10,15,30,0.85)",
            border: "1px solid rgba(100,160,255,0.3)",
            borderRadius: "8px",
            color: "#e8f0ff",
            fontSize: "12px",
            padding: "6px 10px",
            cursor: "pointer",
          }}
        >
          {satellite ? "Map view" : "Satellite view"}
        </button>
        <button
          onClick={fitAllOffices}
          style={{
            background: "rgba(10,15,30,0.85)",
            border: "1px solid rgba(100,160,255,0.3)",
            borderRadius: "8px",
            color: "#e8f0ff",
            fontSize: "12px",
            padding: "6px 10px",
            cursor: "pointer",
          }}
        >
          Reset view
        </button>
      </div>

      <style>{`
        .leaflet-container { background: #ffffff; font-family: inherit; }
        .leaflet-control-attribution { background: rgba(255,255,255,0.75) !important; color: #6b7a99 !important; font-size: 10px !important; }
        .leaflet-control-attribution a { color: #4a6fa5 !important; }
        .leaflet-control-zoom {
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .leaflet-control-zoom a {
          background: #ffffff !important;
          color: #444444 !important;
          border: none !important;
          border-radius: 50% !important;
          width: 32px !important;
          height: 32px !important;
          line-height: 32px !important;
          font-size: 18px !important;
          font-weight: 400 !important;
          display: block !important;
          margin-bottom: 6px !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.18) !important;
          text-align: center !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f0f0f0 !important;
          color: #000 !important;
        }
        .leaflet-bar {
          border: none !important;
          box-shadow: none !important;
        }
        .office-marker-icon { background: transparent !important; border: none !important; }
        .office-dot {
          width: 10px;
          height: 10px;
          background: #567622;
          border: 1px solid #fff;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(120, 160, 40, 0.25);
          cursor: pointer;
          transition: transform 0.2s ease;
        }
        .office-dot:hover {
          transform: scale(1.4);
        }
        .office-tooltip {
          background: rgba(10,15,30,0.92) !important;
          border: 1px solid rgba(100,160,255,0.3) !important;
          border-radius: 10px !important;
          padding: 10px 14px !important;
          min-width: 150px;
          box-shadow: none !important;
        }
        .office-tooltip::before { display: none !important; }
      `}</style>
    </div>
  );
}